import { useCustom } from '@refinedev/core';
import { Spin } from 'antd';
import { BarChart2 } from 'lucide-react';
import { useState, type FC } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ContentDataPoint {
  date: string;
  linkedin: number;
  email: number;
  thread: number;
  blog: number;
}

const COLORS = {
  linkedin: '#3b82f6',
  email: '#22c55e',
  thread: '#8b5cf6',
  blog: '#f97316',
};

const LABELS: Record<string, string> = {
  linkedin: 'LinkedIn',
  email: 'Email',
  thread: 'Thread',
  blog: 'Blog',
};

// Mock data – replaced when backend LOT 26 is ready
const MOCK_DATA: ContentDataPoint[] = [
  { date: '01/02', linkedin: 3, email: 2, thread: 1, blog: 0 },
  { date: '05/02', linkedin: 1, email: 4, thread: 0, blog: 1 },
  { date: '08/02', linkedin: 2, email: 1, thread: 2, blog: 0 },
  { date: '12/02', linkedin: 4, email: 3, thread: 1, blog: 2 },
  { date: '15/02', linkedin: 2, email: 2, thread: 3, blog: 0 },
  { date: '18/02', linkedin: 5, email: 1, thread: 0, blog: 1 },
  { date: '21/02', linkedin: 3, email: 5, thread: 2, blog: 1 },
];

const ContentPerformanceChart: FC = () => {
  const [period] = useState('30d');

  const { data, isLoading } = useCustom<{ data: ContentDataPoint[] }>({
    url: `/api/v1/admin/marketing/content-performance`,
    method: 'get',
    config: { query: { period } },
    queryOptions: { retry: false },
  });

  const rawData = data?.data?.data;
  const chartData = Array.isArray(rawData) ? rawData : MOCK_DATA;

  return (
    <div className="fc-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart2 className="h-5 w-5 text-blue-500 dark:text-blue-400" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Performance Contenus (30j)
          </h3>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Spin />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              className="text-slate-500 dark:text-slate-400"
            />
            <YAxis
              tick={{ fontSize: 11 }}
              allowDecimals={false}
              className="text-slate-500 dark:text-slate-400"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--tooltip-bg, #fff)',
                border: '1px solid var(--tooltip-border, #e2e8f0)',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={((value: number, name: string) => [value, LABELS[name] ?? name]) as never}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
              formatter={(value: string) => LABELS[value] ?? value}
            />
            <Bar dataKey="linkedin" stackId="stack" fill={COLORS.linkedin} radius={[0, 0, 0, 0]} />
            <Bar dataKey="email" stackId="stack" fill={COLORS.email} />
            <Bar dataKey="thread" stackId="stack" fill={COLORS.thread} />
            <Bar dataKey="blog" stackId="stack" fill={COLORS.blog} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ContentPerformanceChart;
